import axios from "axios";
import React, { useEffect, useState, useCallback } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { FilterOutlined, InboxOutlined, ShopOutlined, UserOutlined } from "@ant-design/icons";

import styles from "./orderHistory.module.css";
import GoToHome from "../../Components/GoToHome";
import { getAccessToken, apiUrl } from "../../common/apis";

const { RangePicker } = DatePicker;

function OrderHistory() {
  const [ordersCount, setOrdersCount] = useState(0);
  const [orders, setOrders] = useState([]);
  const [nextPage, setNextPage] = useState("");
  const [activePreset, setActivePreset] = useState("7days");
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, "day"),
    dayjs(),
  ]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const dateFormat = "DD-MM-YYYY";

  const getOrderHistory = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;

    const fromDateQuery = dateRange && dateRange[0]
      ? dateRange[0].format("YYYY-MM-DD 00:00:00")
      : dayjs().subtract(7, "day").format("YYYY-MM-DD 00:00:00");
    const toDateQuery = dateRange && dateRange[1]
      ? dateRange[1].format("YYYY-MM-DD 23:59:59")
      : dayjs().format("YYYY-MM-DD 23:59:59");

    const config = {
      method: "get",
      url: `${apiUrl}order/details/?my_order=false&limit=20&from_date=${fromDateQuery}&to_date=${toDateQuery}`,
      headers: { Authorization: token },
    };

    axios(config)
      .then((res) => {
        if (res.data) {
          setOrdersCount(res.data.count || 0);
          setOrders(res.data.results || []);
          setNextPage(res.data.next || "");
        }
      })
      .catch(() => {});
  }, [dateRange]);

  useEffect(() => {
    if (!getAccessToken()) return;
    getOrderHistory();
  }, [getOrderHistory]);

  const applyPreset = (preset) => {
    setActivePreset(preset);
    const today = dayjs();
    if (preset === "today") {
      setDateRange([today, today]);
    } else if (preset === "yesterday") {
      const yesterday = today.subtract(1, "day");
      setDateRange([yesterday, yesterday]);
    } else if (preset === "7days") {
      setDateRange([today.subtract(7, "day"), today]);
    } else if (preset === "month") {
      setDateRange([today.startOf("month"), today]);
    }
  };

  const getOrderTime = (time) => {
    if (!time) return "";
    const utcDateTime = new Date(time);
    const istDateTime = utcDateTime
      .toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    return istDateTime;
  };

  const loadMoreOrders = () => {
    if (!nextPage) return;
    setIsLoadingMore(true);
    let config = {
      method: "get",
      url: nextPage.startsWith("http") ? nextPage : `${apiUrl}${nextPage}`,
      headers: { Authorization: getAccessToken() },
    };

    axios(config)
      .then((res) => {
        setIsLoadingMore(false);
        if (res.data?.results) {
          setOrders((prev) => [...prev, ...res.data.results]);
          setNextPage(res.data.next || "");
        }
      })
      .catch(() => {
        setIsLoadingMore(false);
      });
  };

  return (
    <div className={styles.historyContainer}>
      <GoToHome />

      {/* Header */}
      <div className={styles.historyHeader}>
        <h2 className={styles.historyTitle}>Order History</h2>
        <span className={styles.totalBadge}>{`${ordersCount} Delivered`}</span>
      </div>

      {/* Filter Section */}
      <div className={styles.filterCard}>
        <span className={styles.filterLabel}>
          <FilterOutlined style={{ marginRight: "6px" }} />
          Filter Period
        </span>

        {/* Quick Presets */}
        <div className={styles.presetContainer}>
          <button
            type="button"
            className={`${styles.presetBtn} ${
              activePreset === "today" ? styles.presetActive : ""
            }`}
            onClick={() => applyPreset("today")}
          >
            Today
          </button>
          <button
            type="button"
            className={`${styles.presetBtn} ${
              activePreset === "yesterday" ? styles.presetActive : ""
            }`}
            onClick={() => applyPreset("yesterday")}
          >
            Yesterday
          </button>
          <button
            type="button"
            className={`${styles.presetBtn} ${
              activePreset === "7days" ? styles.presetActive : ""
            }`}
            onClick={() => applyPreset("7days")}
          >
            Last 7 Days
          </button>
          <button
            type="button"
            className={`${styles.presetBtn} ${
              activePreset === "month" ? styles.presetActive : ""
            }`}
            onClick={() => applyPreset("month")}
          >
            This Month
          </button>
        </div>

        {/* Single Unified RangePicker */}
        <div className={styles.singlePickerRow}>
          <RangePicker
            value={dateRange}
            format={dateFormat}
            allowClear={false}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setDateRange([dates[0], dates[1]]);
                setActivePreset("custom");
              }
            }}
          />
        </div>
      </div>

      {/* Orders List */}
      <div className={styles.orderList}>
        {orders && orders.length > 0 ? (
          orders.map((orderItem) => (
            <div key={orderItem.id} className={styles.orderItemContainer}>
              <div className={styles.cardTop}>
                <span className={styles.orderId}>{`#FDR ${orderItem.id}`}</span>
                <span className={styles.orderDate}>
                  {getOrderTime(orderItem.date || orderItem.payment?.date)}
                </span>
              </div>

              <div className={styles.cardBody}>
                <div className={styles.rowItem}>
                  <span>
                    <ShopOutlined style={{ marginRight: "6px", color: "#d9363e" }} />
                    <strong>{orderItem.branch?.name || "Restaurant"}</strong>
                  </span>
                </div>

                <div className={styles.rowItem}>
                  <span>
                    <UserOutlined style={{ marginRight: "6px", color: "#64748b" }} />
                    {orderItem.user?.first_name || "Customer"}
                  </span>
                  <span className={styles.badgePayment}>
                    {orderItem.order_type} • ₹{orderItem.actual_amount_collected}
                  </span>
                </div>

                {Number(orderItem.delivery_boy_tip) > 0 && (
                  <div className={styles.rowItem} style={{ marginTop: "4px" }}>
                    <span style={{ fontSize: "12px", color: "#166534" }}>Delivery Tip:</span>
                    <span className={styles.badgeTip}>
                      +₹{orderItem.delivery_boy_tip}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <InboxOutlined />
            </div>
            <p className={styles.emptyText}>No completed orders found in this date range.</p>
          </div>
        )}
      </div>

      {nextPage && (
        <button
          className={styles.loadMoreBtn}
          onClick={loadMoreOrders}
          disabled={isLoadingMore}
        >
          {isLoadingMore ? "Loading..." : "Load More Orders"}
        </button>
      )}
    </div>
  );
}

export default OrderHistory;
